// src/components/EnvirondecExplorer.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator
} from "./ui/dropdown-menu";
import { Loader2, Search, ChevronLeft, ChevronRight, ArrowRightLeft } from "lucide-react";

import {
  fetchEnvirondecFilters,
  searchEnvirondecEpds,
  fetchEnvirondecDetails,
  parseEnvirondecIlcd
} from "../services/environdecService";

const UI_PAGE_SIZE = 50;
const MIN_QUERY_LEN = 2;
const SAMPLE_QUERIES = ["concrete", "timber", "insulation", "steel", "gypsum"];

function formatImpactValue(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  const absValue = Math.abs(num);
  if (absValue >= 1000) return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  if (absValue >= 1) return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (absValue === 0) return "0";
  if (absValue < 0.001) return num.toExponential(2);
  return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatSelectionSummary(values, { fallback = "All" } = {}) {
  if (!Array.isArray(values) || values.length === 0) return fallback;
  if (values.length <= 2) return values.join(", ");
  return `${values.slice(0, 2).join(", ")} +${values.length - 2}`;
}

function toggleValue(id, checked, setter) {
  setter((prev) => {
    const next = new Set(prev);
    if (checked) next.add(id);
    else next.delete(id);
    return Array.from(next);
  });
}

function formatDateMaybe(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function asArray(value) {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

const ImpactCategoryTable = ({ impact, declaredUnitLabel }) => {
  const modules = impact?.modules || {};
  const rows = Object.entries(modules);
  const ordered = ["A1-A3", "A4", "A5", "B1", "B2", "B3", "B4", "B5", "B6", "B7", "C1", "C2", "C3", "C4", "D"];
  rows.sort((a, b) => ordered.indexOf(a[0]) - ordered.indexOf(b[0]));

  return (
    <Card className="shadow-sm border-emerald-100/80 bg-white">
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base text-emerald-700 flex items-center gap-2">
            <span>{impact?.description || "Impact"}</span>
            {impact?.unit && (
              <Badge variant="secondary" className="text-xs bg-emerald-50 text-emerald-700">
                {impact.unit} / {declaredUnitLabel || "declared unit"}
              </Badge>
            )}
          </CardTitle>
          <Badge className="bg-emerald-600 text-white text-xs">
            {formatImpactValue(impact?.total)} {impact?.unit || ""}
          </Badge>
        </div>
        <CardDescription className="text-xs">Impacts per {declaredUnitLabel || "declared unit"}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-lg border border-emerald-100/70">
          <table className="w-full text-sm">
            <thead className="bg-emerald-50 text-emerald-700">
              <tr>
                <th className="text-left font-medium px-3 py-2">Module</th>
                <th className="text-right font-medium px-3 py-2">Impact / {declaredUnitLabel || "declared unit"}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} className="odd:bg-white even:bg-emerald-50/40">
                  <td className="px-3 py-2 font-medium text-emerald-800">{k}</td>
                  <td className="px-3 py-2 text-right text-emerald-900">{formatImpactValue(v)}</td>
                </tr>
              ))}
              <tr className="bg-emerald-100/60 text-emerald-900 font-semibold">
                <td className="px-3 py-2">Total</td>
                <td className="px-3 py-2 text-right">{formatImpactValue(impact?.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

const ResultItem = ({ item, isSelected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(item)}
    className={`w-full text-left p-4 rounded-xl border transition shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500/60 flex flex-col gap-1 ${
      isSelected ? "border-emerald-500 bg-emerald-50/70" : "border-emerald-100 bg-white/80"
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <span className="font-semibold text-gray-800 line-clamp-2">{item.title || "EPD record"}</span>
      <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[11px]">
        {item.isSectorEPD ? "Industry" : "Generic"}
      </Badge>
    </div>
    <div className="text-xs text-gray-600 flex flex-wrap gap-2">
      {item.companyName && (
        <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
          {item.companyName}
        </Badge>
      )}
      {item.productCategoryName && (
        <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
          {item.productCategoryName}
        </Badge>
      )}
      {item.ownerCountry && (
        <Badge variant="outline" className="border-emerald-200 text-emerald-700 bg-white">
          {item.ownerCountry}
        </Badge>
      )}
    </div>
    {item.updatedAt && (
      <span className="text-[11px] text-gray-500">Updated {formatDateMaybe(item.updatedAt)}</span>
    )}
  </button>
);

const SkeletonItem = () => (
  <div className="w-full p-4 rounded-xl border border-emerald-100 bg-white/80 shadow-sm">
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-emerald-100/70 rounded w-3/4" />
      <div className="flex gap-2">
        <div className="h-5 bg-emerald-50 rounded w-20" />
        <div className="h-5 bg-emerald-50 rounded w-24" />
        <div className="h-5 bg-emerald-50 rounded w-16" />
      </div>
    </div>
  </div>
);
const EnvirondecExplorer = ({ onShortlist, shortlist = [], onStatusChange, onRegisterRefresh }) => {
  const inputRef = useRef(null);
  const activeControllerRef = useRef(null);

  const [filtersStatus, setFiltersStatus] = useState("loading");
  const [filtersError, setFiltersError] = useState(null);
  const [filters, setFilters] = useState({ countries: [], categories: [], basisOptions: [], materialProperties: [] });

  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sectorFilter, setSectorFilter] = useState("all");

  const [searchTerm, setSearchTerm] = useState("");
  const [committedQuery, setCommittedQuery] = useState("");
  const [committedFilters, setCommittedFilters] = useState({});
  const [searchNonce, setSearchNonce] = useState(0);

  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchError, setSearchError] = useState(null);
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const [selectedItem, setSelectedItem] = useState(null);
  const [detailStatus, setDetailStatus] = useState("idle");
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);

  const [ilcdStatus, setIlcdStatus] = useState("idle");
  const [ilcdSummary, setIlcdSummary] = useState(null);
  const [ilcdError, setIlcdError] = useState(null);

  const loadFilters = useCallback(async () => {
    setFiltersStatus("loading");
    setFiltersError(null);
    try {
      const data = await fetchEnvirondecFilters();
      setFilters({
        countries: Array.isArray(data?.countries) ? data.countries : [],
        categories: Array.isArray(data?.categories) ? data.categories : [],
        basisOptions: Array.isArray(data?.basisOptions) ? data.basisOptions : [],
        materialProperties: Array.isArray(data?.materialProperties) ? data.materialProperties : []
      });
      setFiltersStatus("success");
    } catch (err) {
      setFiltersStatus("error");
      setFiltersError(String(err?.message || err));
    }
  }, []);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    if (typeof onRegisterRefresh === "function") onRegisterRefresh(loadFilters);
  }, [onRegisterRefresh, loadFilters]);

  useEffect(() => {
    if (typeof onStatusChange === "function") {
      onStatusChange({ status: filtersStatus, error: filtersError });
    }
  }, [filtersStatus, filtersError, onStatusChange]);

  const commitSearch = useCallback(() => {
    const trimmed = searchTerm.trim();
    if (trimmed && trimmed.length < MIN_QUERY_LEN) {
      setSearchStatus("idle");
      setSearchError(null);
      setItems([]);
      setHasMore(false);
      return;
    }
    setCommittedQuery(trimmed);
    setCommittedFilters({
      countries: selectedCountries,
      categories: selectedCategories,
      sector: sectorFilter
    });
    setPageNumber(1);
    setSearchNonce((n) => n + 1);
  }, [searchTerm, selectedCountries, selectedCategories, sectorFilter]);

  const handleSelectSample = useCallback(
    (sample) => {
      setSearchTerm(sample);
      inputRef.current?.focus();
    },
    []
  );

  useEffect(() => {
    const hasCommittedFilters =
      (committedFilters.countries || []).length > 0 ||
      (committedFilters.categories || []).length > 0 ||
      (committedFilters.sector && committedFilters.sector !== "all");

    if (!committedQuery && !hasCommittedFilters && searchNonce === 0) {
      setSearchStatus("idle");
      setSearchError(null);
      setItems([]);
      setHasMore(false);
      return;
    }

    const controller = new AbortController();
    if (activeControllerRef.current) activeControllerRef.current.abort();
    activeControllerRef.current = controller;

    const runSearch = async () => {
      setSearchStatus("loading");
      setSearchError(null);
      try {
        const result = await searchEnvirondecEpds({
          searchString: committedQuery,
          country: committedFilters.countries || [],
          category: committedFilters.categories || [],
          sector: committedFilters.sector === "all" ? "" : committedFilters.sector,
          pageNumber,
          pageSize: UI_PAGE_SIZE,
          signal: controller.signal
        });
        setItems(Array.isArray(result?.items) ? result.items : []);
        setHasMore(Boolean(result?.hasMore));
        setSearchStatus("success");
      } catch (err) {
        if (err?.name === "AbortError") return;
        setSearchStatus("error");
        setSearchError(String(err?.message || err));
      }
    };

    runSearch();
    return () => controller.abort();
  }, [committedQuery, committedFilters, pageNumber, searchNonce]);

  const handleSelect = useCallback(async (item) => {
    setSelectedItem(item);
    setDetailStatus("loading");
    setDetailError(null);
    setDetail(null);
    setIlcdStatus("idle");
    setIlcdSummary(null);
    setIlcdError(null);
    try {
      const detailResponse = await fetchEnvirondecDetails({ registrationNumber: item.registrationNumber });
      setDetail(detailResponse);
      setDetailStatus("success");
    } catch (err) {
      setDetailStatus("error");
      setDetailError(String(err?.message || err));
    }
  }, []);

  const requestIlcd = useCallback(async () => {
    if (!selectedItem?.registrationNumber) return;
    setIlcdStatus("loading");
    setIlcdError(null);
    try {
      const summary = await parseEnvirondecIlcd({ registrationNumber: selectedItem.registrationNumber });
      setIlcdSummary(summary);
      setIlcdStatus("success");
    } catch (err) {
      setIlcdStatus("error");
      setIlcdError(String(err?.message || err));
    }
  }, [selectedItem]);

  const detailData = detail?.data || null;
  const detailSummary = detail?.summary || null;
  const declaredUnitLabel = ilcdSummary?.declaredUnit?.declaredUnitName || null;

  const shortlistImpact = useMemo(() => {
    if (!ilcdSummary?.impacts?.length) return null;
    const candidate = ilcdSummary.impacts.find((impact) => /gwp|global warming/i.test(impact.description || ""));
    return candidate || ilcdSummary.impacts[0];
  }, [ilcdSummary]);

  const shortlistModules = useMemo(() => {
    if (!shortlistImpact?.modules) return null;
    return shortlistImpact.modules;
  }, [shortlistImpact]);

  const shortlistGwpTotal = useMemo(() => {
    if (!shortlistImpact?.total) return 0;
    return Number(shortlistImpact.total) || 0;
  }, [shortlistImpact]);

  const isAlreadyShortlisted = useMemo(() => {
    const uuid = selectedItem?.registrationNumber
      ? `epd-international:${selectedItem.registrationNumber}`
      : null;
    if (!uuid) return false;
    return Array.isArray(shortlist) && shortlist.some((s) => String(s.uuid) === String(uuid));
  }, [selectedItem, shortlist]);

  const fileLinks = useMemo(() => {
    if (!detailData) return [];
    const reg = detailData.registrationNumber || selectedItem?.registrationNumber;
    const reportFiles = asArray(detailData.reportFiles);
    const imageFiles = asArray(detailData.images);
    const lcaFile = detailData.lcaFile ? [detailData.lcaFile] : [];

    const buildLink = (fileId, label) => {
      if (!fileId) return null;
      const isUrl = /^https?:\/\//i.test(fileId);
      return {
        id: fileId,
        label,
        href: isUrl
          ? fileId
          : `/api/epd-international?op=file&registrationNumber=${encodeURIComponent(reg)}&fileId=${encodeURIComponent(
              fileId
            )}`,
        external: isUrl
      };
    };

    return [
      ...reportFiles.map((id, idx) => buildLink(id, `Report file ${idx + 1}`)),
      ...lcaFile.map((id) => buildLink(id, "LCA file")),
      ...imageFiles.map((id, idx) => buildLink(id, `Image ${idx + 1}`))
    ].filter(Boolean);
  }, [detailData, selectedItem]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="space-y-0 pt-1 sm:pt-0">
          <div className="flex flex-col gap-2" style={{ marginBottom: "10px" }}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                <Input
                  ref={inputRef}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                  placeholder="Search EPDs, or leave empty and use filters"
                  className="pl-9 pr-12 h-9 text-xs text-gray-900"
                />
                {searchStatus === "loading" && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500 animate-spin" />
                )}
              </div>

              <Button
                type="button"
                className="h-9 px-3 bg-emerald-600 text-white hover:bg-emerald-700 w-full sm:w-auto"
                disabled={filtersStatus !== "success" || searchStatus === "loading"}
                onClick={commitSearch}
              >
                Search
              </Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600" style={{ marginBottom: "16px" }}>
            {SAMPLE_QUERIES.map((sample) => (
              <Badge
                key={sample}
                onClick={() => handleSelectSample(sample)}
                className={`bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200 shadow-none py-1 px-2.5 ${
                  String(sample || "").toLowerCase() === "gypsum" ? "hidden sm:inline-flex" : ""
                }`}
              >
                {sample}
              </Badge>
            ))}
          </div>

          <div className="text-xs text-gray-600 mt-2" style={{ marginBottom: "12px" }}>
            <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                    disabled={filters.countries.length === 0}
                  >
                    <span>Country</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">
                      {formatSelectionSummary(selectedCountries)}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Country</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {filters.countries.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>
                      No countries
                    </DropdownMenuItem>
                  ) : (
                    filters.countries.map((o) => (
                      <DropdownMenuCheckboxItem
                        key={o.id}
                        checked={selectedCountries.includes(o.name)}
                        className="text-xs"
                        onCheckedChange={(checked) => toggleValue(o.name, checked, setSelectedCountries)}
                      >
                        {o.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => {
                      e.preventDefault();
                      setSelectedCountries([]);
                    }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                    disabled={filters.categories.length === 0}
                  >
                    <span>Category</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">
                      {formatSelectionSummary(selectedCategories)}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-64">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">Category</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {filters.categories.length === 0 ? (
                    <DropdownMenuItem className="text-xs text-gray-500" disabled>
                      No categories
                    </DropdownMenuItem>
                  ) : (
                    filters.categories.map((o) => (
                      <DropdownMenuCheckboxItem
                        key={o.id}
                        checked={selectedCategories.includes(o.name)}
                        className="text-xs"
                        onCheckedChange={(checked) => toggleValue(o.name, checked, setSelectedCategories)}
                      >
                        {o.name}
                      </DropdownMenuCheckboxItem>
                    ))
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-xs text-emerald-700"
                    onSelect={(e) => {
                      e.preventDefault();
                      setSelectedCategories([]);
                    }}
                  >
                    Clear selection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 w-full sm:w-auto min-w-0 sm:min-w-[160px] justify-between gap-2 border-emerald-200 bg-white text-emerald-700"
                  >
                    <span>EPD type</span>
                    <span className="max-w-[100px] truncate font-medium text-emerald-600">
                      {sectorFilter === "all" ? "All" : sectorFilter === "generic" ? "Generic" : "Industry"}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <DropdownMenuLabel className="text-xs font-semibold text-emerald-700">EPD type</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {[
                    { id: "all", label: "All" },
                    { id: "generic", label: "Generic (non-sector)" },
                    { id: "industry", label: "Industry (sector)" }
                  ].map((opt) => (
                    <DropdownMenuItem
                      key={opt.id}
                      className="text-xs"
                      onSelect={(e) => {
                        e.preventDefault();
                        setSectorFilter(opt.id);
                      }}
                    >
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-8 px-3 text-xs text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={() => {
                  setSelectedCountries([]);
                  setSelectedCategories([]);
                  setSectorFilter("all");
                }}
                disabled={selectedCountries.length === 0 && selectedCategories.length === 0 && sectorFilter === "all"}
              >
                Clear all
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-6 text-justify sm:text-left">
            Tip: EPD International is rate limited. Keep searches focused and rely on filters where possible.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]">
        <Card className="bg-white border border-emerald-100 shadow self-start h-[650px] flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-800">Matching materials</CardTitle>
            <CardDescription>
              {searchStatus === "idle"
                ? "Enter at least two characters to search."
                : searchStatus === "loading"
                ? "Searching..."
                : items.length
                ? `Showing ${items.length} items`
                : "No results"}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            {searchError && (
              <Alert variant="destructive" className="mb-4 flex-shrink-0">
                <AlertTitle>Search error</AlertTitle>
                <AlertDescription>{searchError}</AlertDescription>
              </Alert>
            )}
            {searchStatus === "idle" && (
              <div className="mb-3 text-sm text-gray-400 bg-emerald-50/30 border border-dashed border-emerald-200/50 rounded-xl p-4">
                Start typing a product name or use filters to explore EPD International.
              </div>
            )}

            <ScrollArea className="flex-1 min-h-0 mt-2 pr-2">
              <div className="space-y-3">
                {searchStatus === "loading" && items.length === 0
                  ? Array.from({ length: 8 }).map((_, idx) => <SkeletonItem key={`sk-${idx}`} />)
                  : items.map((it) => (
                      <ResultItem
                        key={it.registrationNumber}
                        item={it}
                        isSelected={String(selectedItem?.registrationNumber) === String(it.registrationNumber)}
                        onSelect={handleSelect}
                      />
                    ))}
              </div>
            </ScrollArea>

            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-xs text-emerald-800">
              <span className="font-medium text-emerald-900">Page {pageNumber}</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                  disabled={pageNumber <= 1 || searchStatus === "loading"}
                  className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  onClick={() => setPageNumber((p) => p + 1)}
                  disabled={!hasMore || searchStatus === "loading"}
                  className="h-7 w-7 text-emerald-700 hover:text-emerald-900 disabled:text-gray-300"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border border-emerald-100 self-start h-[650px] flex flex-col overflow-hidden">
          <CardHeader>
            <CardTitle className="text-gray-800 flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-emerald-600" />
              Material impact details
            </CardTitle>
            <CardDescription>Select a record to load details, files, and ILCD data.</CardDescription>
          </CardHeader>
          <CardContent className="flex-1 min-h-0 flex flex-col">
            <ScrollArea className="flex-1 min-h-0 pr-2">
              <div className="space-y-5">
                {detailStatus === "idle" && (
                  <div className="text-sm text-gray-400 bg-emerald-50/30 border border-emerald-200/50 rounded-xl p-4">
                    Choose an item on the left to load full EPD International details.
                  </div>
                )}

                {detailStatus === "loading" && (
                  <div className="flex items-center gap-3 text-emerald-700">
                    <Loader2 className="w-5 h-5 animate-spin" /> Fetching record details...
                  </div>
                )}

                {detailStatus === "error" && (
                  <Alert variant="destructive">
                    <AlertTitle>Could not load record</AlertTitle>
                    <AlertDescription>{detailError}</AlertDescription>
                  </Alert>
                )}

                {detailStatus === "success" && detailData && (
                  <div className="space-y-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-4 space-y-2">
                      <h3 className="text-lg font-semibold text-emerald-900">
                        {detailData.title || detailData.name || "EPD record"}
                      </h3>

                      <div className="text-sm text-gray-700 space-y-1">
                        <p>Company: {detailSummary?.companyName || detailData?.epdOwner?.companyName || "Not specified"}</p>
                        <p>Country: {detailSummary?.ownerCountry || detailData?.epdOwner?.address?.country || "Not specified"}</p>
                        {detailSummary?.productCategoryName && <p>Category: {detailSummary.productCategoryName}</p>}
                        {detailData?.publicationDate && <p>Published: {formatDateMaybe(detailData.publicationDate)}</p>}
                        {detailData?.validUntil && <p>Valid until: {formatDateMaybe(detailData.validUntil)}</p>}
                        {detailData?.pcrFullName && <p>PCR: {detailData.pcrFullName}</p>}
                        {detailData?.cpcrFullName && <p>cPCR: {detailData.cpcrFullName}</p>}
                      </div>

                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        {detailData?.isSectorEPD != null && (
                          <Badge variant="outline" className="border-emerald-300 bg-white">
                            {detailData.isSectorEPD ? "Industry (sector) EPD" : "Generic EPD"}
                          </Badge>
                        )}
                        {detailData?.geographicalScopes?.length > 0 && (
                          <Badge variant="outline" className="border-emerald-300 bg-white">
                            Scope: {detailData.geographicalScopes.join(", ")}
                          </Badge>
                        )}
                      </div>

                      {detailData?.description && (
                        <p className="text-xs text-gray-600">{detailData.description}</p>
                      )}

                      {onShortlist && detailData?.registrationNumber && (
                        <div className="pt-2">
                          {!isAlreadyShortlisted ? (
                            <Button
                              type="button"
                              className="h-9 bg-emerald-600 text-white hover:bg-emerald-700"
                              onClick={() => {
                                const uuid = `epd-international:${detailData.registrationNumber}`;
                                onShortlist({
                                  uuid,
                                  source: "epd-international",
                                  name: detailData.title || detailData.name || "EPD record",
                                  declaredUnit: declaredUnitLabel ? { unit: declaredUnitLabel, amount: 1 } : null,
                                  gwpTotal: shortlistGwpTotal,
                                  modules: shortlistModules,
                                  materialProperties: null,
                                  geography: detailSummary?.ownerCountry || null,
                                  manufacturer: detailSummary?.companyName || null
                                });
                              }}
                            >
                              Shortlist
                            </Button>
                          ) : (
                            <Button
                              type="button"
                              variant="outline"
                              className="h-9 border-emerald-300 text-emerald-700"
                              onClick={() =>
                                onShortlist({
                                  uuid: `epd-international:${detailData.registrationNumber}`,
                                  __remove: true
                                })
                              }
                            >
                              Remove from shortlist
                            </Button>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-emerald-100 bg-white p-4 space-y-2">
                      <h4 className="text-sm font-semibold text-emerald-800">Files</h4>
                      {fileLinks.length === 0 ? (
                        <p className="text-xs text-gray-500">No public files listed.</p>
                      ) : (
                        <div className="flex flex-col gap-2">
                          {fileLinks.map((file) => (
                            <a
                              key={`${file.id}-${file.label}`}
                              href={file.href}
                              target={file.external ? "_blank" : undefined}
                              rel={file.external ? "noreferrer" : undefined}
                              className="text-xs text-emerald-700 underline"
                            >
                              {file.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="rounded-xl border border-emerald-100 bg-white p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-emerald-800">ILCD summary</h4>
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 bg-emerald-600 text-white hover:bg-emerald-700"
                          onClick={requestIlcd}
                          disabled={ilcdStatus === "loading"}
                        >
                          {ilcdStatus === "loading" ? "Parsing..." : "Download & parse ILCD"}
                        </Button>
                      </div>

                      {ilcdStatus === "error" && (
                        <Alert variant="destructive">
                          <AlertTitle>ILCD parse failed</AlertTitle>
                          <AlertDescription>{ilcdError}</AlertDescription>
                        </Alert>
                      )}

                      {ilcdStatus === "success" && ilcdSummary && (
                        <div className="space-y-4">
                          {declaredUnitLabel && (
                            <Badge variant="outline" className="border-emerald-300 bg-white">
                              Declared unit: {declaredUnitLabel}
                            </Badge>
                          )}

                          {ilcdSummary.impacts && ilcdSummary.impacts.length > 0 ? (
                            ilcdSummary.impacts.map((impact) => (
                              <ImpactCategoryTable
                                key={impact.description}
                                impact={impact}
                                declaredUnitLabel={declaredUnitLabel}
                              />
                            ))
                          ) : (
                            <div className="text-xs text-gray-500">
                              No impact table parsed yet. Use raw download for manual review.
                            </div>
                          )}

                          {ilcdSummary.files?.length > 0 && (
                            <div className="text-xs text-gray-500">
                              Bundle files: {ilcdSummary.files.slice(0, 6).join(", ")}
                              {ilcdSummary.files.length > 6 ? "..." : ""}
                            </div>
                          )}

                          {ilcdSummary.rawDownloadUrl && (
                            <a href={ilcdSummary.rawDownloadUrl} className="text-xs text-emerald-700 underline">
                              Download raw ILCD zip
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnvirondecExplorer;
